import { useEffect, useRef, useState } from 'react';
import { Avatar, Box, Divider, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faThumbtack,
  faChevronUp,
  faBadgeCheck,
} from '@fortawesome/pro-solid-svg-icons';
import { Post, ReactionType } from '../../../../generated/graphql';
import { usePinnedPosts } from '../hooks/usePinnedPosts';
import { PATHS, navigate } from '@client/routes';
import { fromNow } from '../utils/dateTimeUtils';
import { ActionMenu } from './ActionMenu';
import { PostContent } from './PostContent';
import { PostReactions } from './PostReactions';
import { PostFooter } from './PostFooter';
import auth from '@client/components/auth';
import { getReplyInDMHandler } from '../utils/replyInDM';
import { mixTrack } from '@services/mixpanel';

type Props = {
  communityId: number;
  influencerId: number;
  isUserCommunityOwner: boolean;
  isDmFeatureEnabled: boolean;
  onUnpin: (postId: string, post: Post) => void;
  onBlock: (communityId: number, userId: number) => void;
  onToggleReaction: (postId: string, type: ReactionType) => Promise<unknown>;
  onDelete: (postId: string) => void;
  onGoToPost?: (postId: string) => void;
};

export const PinnedPostsSection = ({
  communityId,
  influencerId,
  isUserCommunityOwner,
  isDmFeatureEnabled,
  onUnpin,
  onBlock,
  onToggleReaction,
  onDelete,
  onGoToPost,
}: Props) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [bodyHeight, setBodyHeight] = useState<number | undefined>(undefined);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const { data: pinnedPosts, isLoading } = usePinnedPosts(communityId);
  const isAdminModerator = auth.isAdministrator() || auth.isModerator();
  const isAdmin = auth.isAdministrator();

  const goToPost = (postId: string) => {
    const selection = window.getSelection();
    if (selection && selection.toString().length > 0) {
      return;
    }
    navigate(PATHS.COMMUNITY_POST(influencerId, communityId, postId));
  };

  const showPinAction = isUserCommunityOwner || isAdmin;
  const toggleCollapsed = () => setIsCollapsed((prev) => !prev);

  useEffect(() => {
    const node = bodyRef.current;
    if (!node) return;
    const measure = () => setBodyHeight(node.scrollHeight);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [pinnedPosts]);

  if (isLoading || !pinnedPosts?.length) {
    return null;
  }

  return (
    <Box className="bg-[#EDE5FC] rounded-[10px] p-2 mb-4">
      {/* Header */}
      <Box
        className="flex items-center justify-between px-2 py-1 cursor-pointer select-none"
        onClick={toggleCollapsed}
      >
        <Box className="flex items-center gap-6">
          <Box className="flex items-center gap-3">
            <FontAwesomeIcon
              icon={faThumbtack}
              className="text-black text-[12px]"
            />
          </Box>
          <Typography
            className="text-[12px] uppercase tracking-[1px]"
            sx={{ color: '#000', fontWeight: 500 }}
          >
            Pinned Posts
          </Typography>
        </Box>
        <Box className="flex items-center gap-2">
          <Typography
            className="text-[14px]"
            sx={{ color: '#131313', fontWeight: 500 }}
          >
            {isCollapsed ? 'Show' : 'Hide'}
          </Typography>
          <Typography
            className="text-[12px] uppercase tracking-[1px]"
            sx={{ color: '#000', fontWeight: 500 }}
          >
            ({pinnedPosts.length})
          </Typography>
        </Box>
      </Box>

      {/* Posts */}
      <Box
        ref={bodyRef}
        className="flex flex-col overflow-hidden"
        style={{
          maxHeight: isCollapsed ? 0 : bodyHeight,
          transition: 'max-height 220ms ease',
        }}
      >
        {pinnedPosts.map((post, index) => {
          const author = post.author;
          const isAuthor = author.badges.includes('own');
          const isCreatorsPost = author.badges.includes('creator');
          const showBlockUser =
            ((isUserCommunityOwner && !isAuthor) || isAdmin) && !isCreatorsPost;

          return (
            <Box key={post.id}>
              {index > 0 && <Divider className="mx-4" />}
              <Box
                className="p-4 cursor-pointer"
                onClick={() => goToPost(post.id)}
              >
                {/* Post Header */}
                <Box className="flex items-center justify-between mb-4">
                  <Box className="flex items-center gap-2.5">
                    <Avatar
                      alt={author.username}
                      src={author.profilePicture || ''}
                      sx={{ width: 40, height: 40 }}
                    />
                    <Box className="flex flex-col justify-center">
                      <Box className="flex gap-1.5 items-center">
                        <Typography
                          sx={{
                            fontWeight: 900,
                            fontSize: '16px',
                            lineHeight: 1.4,
                          }}
                        >
                          {author.username}
                        </Typography>
                        {author.badges.includes('creator') && (
                          <FontAwesomeIcon
                            icon={faBadgeCheck}
                            className="text-[#5F1ECC] text-[15px]"
                          />
                        )}
                      </Box>
                      <Typography
                        sx={{
                          fontWeight: 400,
                          fontSize: '12px',
                          lineHeight: 1.25,
                          color: '#2C3548',
                        }}
                      >
                        {fromNow(post.createdAt)}
                      </Typography>
                    </Box>
                  </Box>
                  {(isUserCommunityOwner || isAuthor || isAdmin) && (
                    <ActionMenu
                      onDelete={() => onDelete(post.id)}
                      onBlock={() => onBlock(communityId, author.id)}
                      item="post"
                      showBlockUser={showBlockUser}
                      userEmail={author.email}
                      onReplyInDM={getReplyInDMHandler({
                        author: post.author,
                        isOwner: isUserCommunityOwner,
                        isAdminModerator,
                        influencerId,
                        isInfluencer: auth.isInfluencer(),
                        isDmFeatureEnabled,
                      })}
                      showPinAction={showPinAction}
                      isPinned={true}
                      onPin={() => onUnpin(post.id, post)}
                      onGoToPost={
                        onGoToPost ? () => onGoToPost(post.id) : undefined
                      }
                    />
                  )}
                </Box>
                {/* Post Content with Media */}
                <PostContent
                  text={post.text}
                  media={post.media}
                  isPostPage={false}
                  isPinned
                />
                {/* Reactions - wrapped to prevent navigation on click */}
                <Box onClick={(e) => e.stopPropagation()}>
                  <PostReactions
                    reactions={post.reactions || []}
                    onToggleReaction={(type) => {
                      onToggleReaction(post.id, type);
                    }}
                  />
                </Box>
                {/* Footer with comments */}
                <PostFooter
                  reactions={post.reactions || []}
                  onAddComment={() => {
                    mixTrack({
                      type: 'Comment Added',
                      payload: {
                        'Post Type': author.badges.includes('creator')
                          ? 'Creator'
                          : 'Consumer',
                        'Media Type': post.media
                          .map((m) => (m.video ? 'video' : 'image'))
                          .join(', '),
                      },
                    });
                    goToPost(post.id);
                  }}
                />
              </Box>
            </Box>
          );
        })}
        {/* Collapse button at bottom */}
        {!isCollapsed && (
          <Box
            className="flex justify-center py-2 cursor-pointer"
            onClick={() => setIsCollapsed(true)}
          >
            <FontAwesomeIcon
              icon={faChevronUp}
              className="text-black text-[12px]"
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};
